import { useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { Save, Search } from "lucide-react";
import { Panel, Inp, Sel, YN, Btn, Lbl, RT } from "./ui";

export default function SectionEms({ rep, canEdit, onChanged }) {
  const [f, setF] = useState(rep.ems_fire);
  const [destQ, setDestQ] = useState("");
  const [destOpts, setDestOpts] = useState([]);
  const P = (k) => ({ value: f[k], onChange: (v) => setF({ ...f, [k]: v }), disabled: !canEdit });
  const searchDest = async (q) => {
    setDestQ(q);
    if (q.trim().length < 2) { setDestOpts([]); return; }
    try {
      const r = await client.get(`/facilities?q=${encodeURIComponent(q.trim())}`);
      setDestOpts((r.data.facilities || []).slice(0, 6));
    } catch { /* ignore */ }
  };
  const save = async () => {
    try {
      await client.patch(`/rms-reports/${rep.id}/section`, { section: "ems_fire", data: f });
      toast.success("EMS / Fire information saved");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <Panel title="EMS / Fire Information" testid="panel-ems"
      right={canEdit && <Btn small onClick={save} testid="ems-save"><Save size={11} /> Save</Btn>}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Inp label="EMS Agency" {...P("agency")} placeholder="Volunteer Medic Squad" testid="ems-agency" />
        <Inp label="Unit Number" {...P("unit_number")} placeholder="415" testid="ems-unit" />
        <Sel label="Response Type" {...P("response_type")} options={["BLS", "ALS", "Fire", "Rescue", "Specialty"]} testid="ems-response-type" />
        <YN label="Patient Transported" {...P("transported")} testid="ems-transported" />
        <Sel label="Transport Level" {...P("transport_level")} options={["BLS", "ALS", "AIR MEDICAL", "OTHER"]} testid="ems-level" />
        <div className="col-span-2 relative">
          <Lbl>Destination (searchable)</Lbl>
          <div className="flex items-center gap-1">
            <Search size={12} style={{ color: RT.dim }} />
            <input data-testid="ems-destination" value={f.destination || ""} disabled={!canEdit}
              onChange={(e) => { setF({ ...f, destination: e.target.value.toUpperCase() }); searchDest(e.target.value); }}
              placeholder="NORTHWEST HOSPITAL – LIFEBRIDGE HEALTH"
              className="w-full border px-2 py-1 text-[12px] font-mono rounded-sm outline-none disabled:opacity-60"
              style={{ background: RT.input, borderColor: RT.border, color: RT.text }} />
          </div>
          {destOpts.length > 0 && destQ.trim().length >= 2 && (
            <div className="absolute z-20 left-4 right-0 mt-1 border bg-white rounded-sm shadow-md max-h-40 overflow-y-auto" style={{ borderColor: RT.border }}>
              {destOpts.map((d, i) => (
                <button key={i} data-testid={`ems-dest-opt-${i}`}
                  onClick={() => { setF({ ...f, destination: d.name.toUpperCase() }); setDestOpts([]); setDestQ(""); }}
                  className="w-full text-left px-2 py-1.5 text-[11px] font-mono hover:bg-[#eef4f9]" style={{ color: RT.text }}>
                  {d.name} <span style={{ color: RT.dim }}>· {d.city || d.address || d.facility_type}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <Sel label="Patient Disposition" {...P("patient_disposition")}
          options={["Transported", "Treated / Released", "Refused Treatment", "Refused Transport", "No Patient Located", "Deceased", "Other"]} testid="ems-pt-disp" />
        <YN label="Refusal of Care" {...P("refusal")} testid="ems-refusal" />
        <YN label="Naloxone Administered" {...P("naloxone")} unknown testid="ems-naloxone" />
        <YN label="AED Used" {...P("aed")} unknown testid="ems-aed" />
        <Inp label="EMS Incident # (optional)" {...P("ems_incident_number")} testid="ems-incident-num" />
      </div>
      <div className="mt-2">
        <Lbl>EMS Notes (no medical diagnosis required)</Lbl>
        <textarea data-testid="ems-notes" value={f.notes || ""} disabled={!canEdit} rows={3}
          onChange={(e) => setF({ ...f, notes: e.target.value })}
          className="w-full border px-2 py-1 text-[12px] font-mono rounded-sm outline-none disabled:opacity-60"
          style={{ background: RT.input, borderColor: RT.border, color: RT.text }} />
      </div>
    </Panel>
  );
}
